// ProtectedRoute — auth gate.
//
// /me 401 (or no session) → /login. Logged in but must_change_password →
// /change-password (unless already there).
//
// A 5xx / network failure on /me is NOT an auth failure — it must not log the
// user out (a transient backend blip would otherwise bounce a valid session to
// /login). We branch: a genuine 401 → /login (ADR-003: 401 is the canonical
// auth-failure signal, untouched), whether or not an identity is cached; any
// other error → a retry surface ONLY while there is no identity yet (a cold
// load with the server down). A failed background re-check keeps the last
// identity (TanStack keeps `data`), so a signed-in person keeps the page they
// are reading and the frame's cannot-refresh band is the one sign (ADR-074 §9,
// FRONTEND-PATTERNS §Live updates). `useMe` already retries once.
//
// There is NO project-status gate here — that concern lives in the per-route
// /p/:projectId/* tree (the backend 403 PROJECT_SUSPENDED / 404 interceptor
// routes via apiClient). The guard's job is narrow: authenticated-or-not +
// must-change-password + a non-auth-error retry surface.

import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { statusOf } from '../../lib/refusal.js';
import { useMe } from '../../hooks/useMe.js';
import { PageLoader } from '../ui/PageLoader.js';
import { Button } from '../ui/Button.js';
import { ErrorState } from '../layout/ErrorState.js';

export interface ProtectedRouteProps {
  children: ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { data: me, isLoading, isError, error, refetch } = useMe();
  const location = useLocation();
  const { t } = useTranslation('common');

  if (isLoading) {
    // App-boot full-page load — the branded cube loader (PageLoader §loading tiers).
    return <PageLoader />;
  }

  const status = statusOf(error);

  // Genuine 401 → not authenticated → /login, cached identity or not.
  if (isError && status === 401) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  // Non-auth failure (5xx / network / timeout) with no identity yet — don't log
  // out; offer a retry. With an identity cached, fall through: keep the page.
  if (isError && me === undefined) {
    return (
      <ErrorState
        title={t('error.connectionTitle')}
        message={t('error.connectionBody')}
        actions={
          <Button variant="primary" onClick={() => void refetch()}>
            {t('actions.retry')}
          </Button>
        }
      />
    );
  }

  // Defensively: no identity and no error → not authenticated → /login.
  if (!me) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  if (me.user.must_change_password && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  return <>{children}</>;
}
